import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Wine, Grape, Check, ChevronDown, ChevronUp, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { getWinesByRegion, REGIONS } from '@/lib/api';

// Special wine categories available in Cowichan Valley
const SPECIAL_WINES = [
  { id: 'Rosé', name: 'Rosé', emoji: '🌸' },
  { id: 'Sparkling Wine', name: 'Sparkling Wine', emoji: '🥂' },
  { id: 'Cider', name: 'Cider', emoji: '🍎' },
];

export default function WinePreferencesModal({ preferences, onChange, region }) {
  const [isOpen, setIsOpen] = useState(false);
  const [localPreferences, setLocalPreferences] = useState(preferences || {
    wineType: 'any',
    selectedWines: [],
  });

  const [wineVarieties, setWineVarieties] = useState({ reds: [], whites: [] });
  const [isLoadingWines, setIsLoadingWines] = useState(false);
  const [showRedsExpanded, setShowRedsExpanded] = useState(false);
  const [showWhitesExpanded, setShowWhitesExpanded] = useState(false);

  const regionName = REGIONS.find(r => r.value === region)?.label || 'Cowichan Valley';

  useEffect(() => {
    if (region) {
      setIsLoadingWines(true);
      getWinesByRegion(region)
        .then(wines => {
          setWineVarieties(wines);
          setIsLoadingWines(false);
        })
        .catch(() => setIsLoadingWines(false));
    }
  }, [region]);

  const handleOpen = () => {
    setLocalPreferences(preferences || { wineType: 'any', selectedWines: [] });
    setIsOpen(true);
  };

  const handleClose = () => setIsOpen(false);

  const computeAndApply = (newPrefs) => {
    const selectedWines = newPrefs.selectedWines || [];
    let wineType = 'any';
    if (selectedWines.length > 0) {
      const redIds = wineVarieties.reds.map(w => w.id);
      const whiteIds = wineVarieties.whites.map(w => w.id);
      const hasReds = selectedWines.some(id => redIds.includes(id));
      const hasWhites = selectedWines.some(id => whiteIds.includes(id));
      if (hasReds && !hasWhites) wineType = 'reds';
      else if (hasWhites && !hasReds) wineType = 'whites';
      else wineType = 'specific';
    }
    const updated = { ...newPrefs, wineType };
    setLocalPreferences(updated);
    onChange(updated);
  };

  const toggleWineVariety = (wineId) => {
    const selectedWines = localPreferences.selectedWines || [];
    const newSelected = selectedWines.includes(wineId)
      ? selectedWines.filter(id => id !== wineId)
      : [...selectedWines, wineId];
    computeAndApply({ ...localPreferences, selectedWines: newSelected });
  };

  const selectAllReds = () => {
    const redIds = wineVarieties.reds.map(w => w.id);
    const currentSelected = localPreferences.selectedWines || [];
    const allRedsSelected = redIds.every(id => currentSelected.includes(id));
    const newSelected = allRedsSelected
      ? currentSelected.filter(id => !redIds.includes(id))
      : [...new Set([...currentSelected, ...redIds])];
    computeAndApply({ ...localPreferences, selectedWines: newSelected });
  };

  const selectAllWhites = () => {
    const whiteIds = wineVarieties.whites.map(w => w.id);
    const currentSelected = localPreferences.selectedWines || [];
    const allWhitesSelected = whiteIds.every(id => currentSelected.includes(id));
    const newSelected = allWhitesSelected
      ? currentSelected.filter(id => !whiteIds.includes(id))
      : [...new Set([...currentSelected, ...whiteIds])];
    computeAndApply({ ...localPreferences, selectedWines: newSelected });
  };

  const allRedsSelected = wineVarieties.reds.length > 0 &&
    wineVarieties.reds.every(w => localPreferences.selectedWines?.includes(w.id));
  const allWhitesSelected = wineVarieties.whites.length > 0 &&
    wineVarieties.whites.every(w => localPreferences.selectedWines?.includes(w.id));

  const selectedRedCount = wineVarieties.reds.filter(w =>
    localPreferences.selectedWines?.includes(w.id)).length;
  const selectedWhiteCount = wineVarieties.whites.filter(w =>
    localPreferences.selectedWines?.includes(w.id)).length;

  const getSummaryText = () => {
    const prefs = preferences || { wineType: 'any', selectedWines: [] };
    if (prefs.selectedWines?.length > 0) {
      return `${prefs.selectedWines.length} wine${prefs.selectedWines.length > 1 ? 's' : ''} selected`;
    }
    return 'Tap to select wines';
  };

  return (
    <>
      <motion.button
        whileTap={{ scale: 0.98 }}
        onClick={handleOpen}
        className="w-full flex items-center justify-between px-4 py-3 bg-white rounded-xl border border-stone-300 shadow-sm hover:border-[#C9A227]/50 transition-colors"
      >
        <div className="flex items-center gap-3">
          <Grape className="w-6 h-6 text-[#B8860B]" />
          <div className="text-left">
            <p className="font-medium text-stone-800">Choose Wine Types in {regionName}</p>
            <p className="text-sm text-stone-500">{getSummaryText()}</p>
          </div>
        </div>
        <ChevronDown className="w-5 h-5 text-[#722F37]" />
      </motion.button>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-end justify-center"
            onClick={handleClose}
          >
            <motion.div
              initial={{ y: '100%' }}
              animate={{ y: 0 }}
              exit={{ y: '100%' }}
              transition={{ type: 'spring', damping: 25, stiffness: 300 }}
              onClick={(e) => e.stopPropagation()}
              className="w-full max-w-lg bg-white rounded-t-3xl p-6 pb-safe relative max-h-[85vh] overflow-y-auto"
            >
              <div className="flex items-center justify-between mb-6">
                <button
                  onClick={handleClose}
                  className="flex items-center gap-1 text-stone-500 hover:text-stone-700 transition-colors"
                >
                  <X className="w-5 h-5" />
                  <span className="text-sm font-medium">Back</span>
                </button>
                <h2 className="text-lg font-bold text-stone-800">
                  Wines in {regionName}
                </h2>
                <div className="w-16" />
              </div>

              {isLoadingWines && (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-6 h-6 animate-spin text-[#722F37]" />
                  <span className="ml-2 text-stone-500">Loading wines...</span>
                </div>
              )}

              {/* Red Wines */}
              {!isLoadingWines && wineVarieties.reds.length > 0 && (
                <div className="mb-4">
                  <button
                    onClick={() => setShowRedsExpanded(!showRedsExpanded)}
                    className="w-full flex items-center justify-between p-4 bg-[#722F37]/5 rounded-xl border border-[#722F37]/20 hover:bg-[#722F37]/10 transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-[#722F37] flex items-center justify-center">
                        <Wine className="w-5 h-5 text-white" />
                      </div>
                      <div className="text-left">
                        <p className="font-semibold text-[#722F37]">Red Wines</p>
                        <p className="text-xs text-stone-500">
                          {selectedRedCount > 0
                            ? `${selectedRedCount} of ${wineVarieties.reds.length} selected`
                            : `${wineVarieties.reds.length} available`}
                        </p>
                      </div>
                    </div>
                    {showRedsExpanded
                      ? <ChevronUp className="w-5 h-5 text-[#722F37]" />
                      : <ChevronDown className="w-5 h-5 text-[#722F37]" />}
                  </button>

                  <AnimatePresence>
                    {showRedsExpanded && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        className="overflow-hidden"
                      >
                        <div className="pt-3 px-2">
                          <button
                            onClick={selectAllReds}
                            className={cn(
                              "w-full mb-3 py-2 px-4 rounded-lg border text-sm font-medium transition-all",
                              allRedsSelected
                                ? "bg-[#722F37] text-white border-[#722F37]"
                                : "bg-white text-[#722F37] border-[#722F37] hover:bg-[#722F37]/10"
                            )}
                          >
                            {allRedsSelected ? 'Deselect All Reds' : 'Select All Reds'}
                          </button>
                          <div className="grid grid-cols-2 gap-2">
                            {wineVarieties.reds.map((wine) => {
                              const isSelected = localPreferences.selectedWines?.includes(wine.id);
                              return (
                                <button
                                  key={wine.id}
                                  onClick={() => toggleWineVariety(wine.id)}
                                  className={cn(
                                    "flex items-center gap-2 p-3 rounded-lg border text-left text-sm transition-all",
                                    isSelected
                                      ? "border-[#722F37] bg-[#722F37]/10 text-[#722F37]"
                                      : "border-stone-200 bg-white text-stone-600 hover:border-stone-300"
                                  )}
                                >
                                  <div className={cn(
                                    "w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0",
                                    isSelected ? "border-[#722F37] bg-[#722F37]" : "border-stone-300"
                                  )}>
                                    {isSelected && <Check className="w-3 h-3 text-white" />}
                                  </div>
                                  <span className="truncate">{wine.name}</span>
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              )}

              {/* White Wines */}
              {!isLoadingWines && wineVarieties.whites.length > 0 && (
                <div className="mb-6">
                  <button
                    onClick={() => setShowWhitesExpanded(!showWhitesExpanded)}
                    className="w-full flex items-center justify-between p-4 bg-[#C9A227]/5 rounded-xl border border-[#C9A227]/60 hover:bg-[#C9A227]/10 transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-[#C9A227] flex items-center justify-center">
                        <Wine className="w-5 h-5 text-white" />
                      </div>
                      <div className="text-left">
                        <p className="font-semibold text-[#8B7355]">White Wines</p>
                        <p className="text-xs text-stone-500">
                          {selectedWhiteCount > 0
                            ? `${selectedWhiteCount} of ${wineVarieties.whites.length} selected`
                            : `${wineVarieties.whites.length} available`}
                        </p>
                      </div>
                    </div>
                    {showWhitesExpanded
                      ? <ChevronUp className="w-5 h-5 text-[#C9A227]" />
                      : <ChevronDown className="w-5 h-5 text-[#C9A227]" />}
                  </button>

                  <AnimatePresence>
                    {showWhitesExpanded && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        className="overflow-hidden"
                      >
                        <div className="pt-3 px-2">
                          <button
                            onClick={selectAllWhites}
                            className={cn(
                              "w-full mb-3 py-2 px-4 rounded-lg border text-sm font-medium transition-all",
                              allWhitesSelected
                                ? "bg-[#C9A227] text-white border-[#C9A227]"
                                : "bg-white text-[#8B7355] border-[#C9A227] hover:bg-[#C9A227]/10"
                            )}
                          >
                            {allWhitesSelected ? 'Deselect All Whites' : 'Select All Whites'}
                          </button>
                          <div className="grid grid-cols-2 gap-2">
                            {wineVarieties.whites.map((wine) => {
                              const isSelected = localPreferences.selectedWines?.includes(wine.id);
                              return (
                                <button
                                  key={wine.id}
                                  onClick={() => toggleWineVariety(wine.id)}
                                  className={cn(
                                    "flex items-center gap-2 p-3 rounded-lg border text-left text-sm transition-all",
                                    isSelected
                                      ? "border-[#C9A227] bg-[#C9A227]/10 text-[#8B7355]"
                                      : "border-stone-200 bg-white text-stone-600 hover:border-stone-300"
                                  )}
                                >
                                  <div className={cn(
                                    "w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0",
                                    isSelected ? "border-[#C9A227] bg-[#C9A227]" : "border-stone-300"
                                  )}>
                                    {isSelected && <Check className="w-3 h-3 text-white" />}
                                  </div>
                                  <span className="truncate">{wine.name}</span>
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              )}

              {/* Special Wines */}
              <div className="mb-6">
                <h3 className="text-sm font-semibold text-stone-700 mb-3 uppercase tracking-wide">
                  Special Wines
                </h3>
                <div className="grid grid-cols-3 gap-2">
                  {SPECIAL_WINES.map((wine) => {
                    const isSelected = localPreferences.selectedWines?.includes(wine.id);
                    return (
                      <button
                        key={wine.id}
                        onClick={() => toggleWineVariety(wine.id)}
                        className={cn(
                          "flex flex-col items-center gap-1.5 p-3 rounded-xl border-2 transition-all",
                          isSelected
                            ? "border-[#722F37] bg-[#722F37]/10"
                            : "border-stone-200 bg-white hover:border-stone-300"
                        )}
                      >
                        <span className="text-2xl">{wine.emoji}</span>
                        <span className={cn(
                          "text-xs font-medium text-center leading-tight",
                          isSelected ? "text-[#722F37]" : "text-stone-600"
                        )}>
                          {wine.name}
                        </span>
                        {isSelected && (
                          <div className="w-4 h-4 rounded-full bg-[#722F37] flex items-center justify-center">
                            <Check className="w-2.5 h-2.5 text-white" />
                          </div>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>

              {localPreferences.selectedWines?.length > 0 && (
                <div className="mb-4 p-3 bg-stone-50 rounded-xl text-center text-sm text-stone-600">
                  <span>{localPreferences.selectedWines.length} wine{localPreferences.selectedWines.length > 1 ? 's' : ''} selected</span>
                </div>
              )}

              <Button
                onClick={handleClose}
                className="w-full h-14 bg-[#722F37] hover:bg-[#5a252c] text-white text-lg font-semibold rounded-xl ring-2 ring-[#C9A227]/30"
              >
                Done
              </Button>

              <button
                onClick={() => computeAndApply({ wineType: 'any', selectedWines: [] })}
                className="w-full mt-3 py-2 text-stone-500 hover:text-stone-700 text-sm transition-colors"
              >
                Reset to defaults
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
